import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", { name, company, phone });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fv-fade-up max-w-2xl">
      <PageHeader eyebrow="Account" title="Profile" subtitle="Manage your personal and business details." />

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-7 mb-6 flex items-center gap-5">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover" />
        ) : (
          <div className="w-20 h-20 rounded-full fv-gold-gradient flex items-center justify-center text-[#0A0A0B] font-heading text-3xl font-bold">
            {(user?.name || user?.email || "U")[0].toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-heading text-2xl text-white">{user?.name || "—"}</div>
          <div className="text-[#71717A]">{user?.email}</div>
          <div className="mt-1 text-xs text-[#D4AF37] uppercase tracking-wider capitalize">{user?.role} · {user?.subscription?.tier} plan</div>
        </div>
      </div>

      <form onSubmit={save} className="rounded-2xl border border-white/8 bg-[#121214] p-7 space-y-5" data-testid="profile-form">
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Full name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input"
            className="bg-[#0d0d0f] border-white/10 text-white h-11" />
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Company</Label>
          <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="profile-company-input"
            placeholder="Your business name" className="bg-[#0d0d0f] border-white/10 text-white h-11" />
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Phone</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone-input"
            placeholder="+1 (555) 000-0000" className="bg-[#0d0d0f] border-white/10 text-white h-11" />
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Email</Label>
          <Input value={user?.email || ""} disabled className="bg-[#0d0d0f] border-white/10 text-[#71717A] h-11" />
        </div>
        <Button type="submit" disabled={saving} data-testid="profile-save-btn"
          className="fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90 px-8">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </form>
    </div>
  );
}
